"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@repo/ui/lib/utils";
import { type ComponentPropsWithoutRef, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import { CourseButton, type CourseButtonProps } from "@/app/components/CourseLevelPage/CourseButton/CourseButton";
import styles from "./SelectableTile.module.css";
export type SelectableTileSelectButtonProps = CourseButtonProps;

// With this, other components can use :global(.selectable-tile) in their styles
// to define hover, active, or disabled styles for content inside the tile.
//
// e.g. :global(.selectable-tile):hover .some-other-component-class { ... }
const SELECTABLE_TILE_CLASS = "selectable-tile";
export type SelectableTileProps = {
  isSelected?: boolean;
  disabled?: boolean;
  children: ReactNode;
  className?: string;
  /** When set, the whole tile is a selection hit target (desktop). */
  onSelect?: () => void;
};
export function SelectableTile(t0) {
  const $ = _c(15);
  const {
    isSelected,
    disabled: t1,
    children,
    onSelect
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== onSelect) {
    t2 = onSelect && (e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect();
      }
    });
    $[0] = onSelect;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const onKeyDown = t2;
  const t3 = onSelect && styles.tileSelectable;
  let t4;
  if ($[2] !== t3) {
    t4 = cn(SELECTABLE_TILE_CLASS, styles.tile, t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = isSelected || undefined;
  const t6 = disabled || undefined;
  const t7 = onSelect ? "button" : undefined;
  const t8 = onSelect && !disabled ? 0 : -1;
  const t9 = onSelect ? Boolean(isSelected) : undefined;
  const t10 = disabled || undefined;
  const t11 = disabled ? undefined : onSelect;
  const t12 = disabled ? undefined : onKeyDown;
  let t13;
  if ($[4] !== children || $[5] !== t10 || $[6] !== t11 || $[7] !== t12 || $[8] !== t4 || $[9] !== t5 || $[10] !== t6 || $[11] !== t7 || $[12] !== t8 || $[13] !== t9) {
    t13 = <div className={t4} data-selected={t5} data-disabled={t6} role={t7} tabIndex={t8} aria-pressed={t9} aria-disabled={t10} onClick={t11} onKeyDown={t12}>{children}</div>;
    $[4] = children;
    $[5] = t10;
    $[6] = t11;
    $[7] = t12;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  return t13;
}
type SelectableTileContentProps = {
  children: ReactNode;
  className?: string;
  disabled?: boolean;
} & Omit<ComponentPropsWithoutRef<"div">, "children" | "className">;
function SelectableTileContent(t0) {
  const $ = _c(10);
  let children;
  let className;
  let rest;
  if ($[0] !== t0) {
    const {
      children: t1,
      className: t2,
      disabled: _disabled,
      ...t3
    } = t0;
    children = t1;
    className = t2;
    rest = t3;
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = rest;
  } else {
    children = $[1];
    className = $[2];
    rest = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn(styles.content, className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== rest || $[8] !== t1) {
    t2 = <div className={t1} {...rest}>{children}</div>;
    $[6] = children;
    $[7] = rest;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
type SelectableTileListProps = {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** Shared subgrid row count for equal-height sections across columns */
  rows?: number;
};
function SelectableTileList({
  children,
  className,
  style,
  rows
}: SelectableTileListProps) {
  return <div className={cn(styles.tileList, className)} style={rows != null ? {
    ...style,
    ["--tile-rows" as string]: rows
  } : style}>
			{children}
		</div>;
}
SelectableTile.Content = SelectableTileContent;
SelectableTile.SelectButton = CourseButton;
SelectableTile.List = SelectableTileList;